// PORTED VERBATIM from ptc-supervision@d7297e3 src/components/listFilters/filterSurfaceRegistry.ts — the fleet filter
// contract's reference implementation (§14.3: "supervision implements first; each app ports").
// Change it there first, then re-port, so the fleet keeps one copy of the rule.

// Fleet filter contract §6.6 I1 — at most ONE filter surface is open at any moment.
//
// 🔑 "This is a property of the PAGE, not of a component: two independently-managed popovers each
// behaving correctly still produce the screenshot." (Four overlapping popovers, observed in
// ptc-tax-lifecycle, is what produced the rule.) So the state cannot live inside ColumnHeaderFilter
// — one instance per column, none of which can see the others.
//
// A module singleton rather than a React context, deliberately: the three consumer pages
// (ReviewsPage, ClientsPage, ActionItemsPage) each render their headers directly and would every
// one of them need a provider threaded in, which is three chances to forget one and reintroduce
// the defect on a single page. Only one list route is mounted at a time, so a module-scoped "the
// open surface" is the correct scope, not a convenience.
//
// ⚠ Registering does NOT mean rendering: the holder is whatever surface last declared itself open,
// and it is responsible for releasing on close/unmount.

type CloseFn = () => void;

let openSurface: CloseFn | null = null;

/**
 * Declare a surface open. Closes whichever surface was open before, unless it is this same one.
 * Returns nothing — the caller already knows it is open.
 */
export function acquireFilterSurface(close: CloseFn): void {
  if (openSurface && openSurface !== close) {
    const previous = openSurface;
    // Cleared BEFORE invoking, so the closing surface's own release() call is a no-op rather than
    // wiping the incoming holder we are about to set.
    openSurface = null;
    previous();
  }
  openSurface = close;
}

/** Give up the slot, if this surface still holds it. Safe to call when it does not. */
export function releaseFilterSurface(close: CloseFn): void {
  if (openSurface === close) openSurface = null;
}

/** Test-only: no page can leak an open surface into the next test. */
export function resetFilterSurfaceRegistryForTests(): void {
  openSurface = null;
}
